import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import ButtonItem from '@atlaskit/menu/button-item';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';

import { AgentPresenceAvatar } from './AgentPresenceAvatar.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';
import { isAgentPresenceInactive } from './agentPresenceStatus.ts';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.agent-group-menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing the page.',
	},
	editingInactive: {
		id: 'team-presence.agent-group-menu.editing-inactive',
		defaultMessage: 'Editing (Inactive)',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent was editing the page but has been inactive for at least 30 seconds.',
	},
	editingWithActor: {
		id: 'team-presence.agent-group-menu.editing-with-actor',
		defaultMessage: 'with {actorName} • Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing on behalf of a user.',
	},
	editingWithActorInactive: {
		id: 'team-presence.agent-group-menu.editing-with-actor-inactive',
		defaultMessage: 'with {actorName} • Editing (Inactive)',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent was editing on behalf of a user but has been inactive for at least five minutes.',
	},
	viewing: {
		id: 'team-presence.agent-group-menu.viewing',
		defaultMessage: 'Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing the page.',
	},
	viewingInactive: {
		id: 'team-presence.agent-group-menu.viewing-inactive',
		defaultMessage: 'Viewing (Inactive)',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent was viewing the page but has been inactive for at least 30 seconds.',
	},
	viewingWithActor: {
		id: 'team-presence.agent-group-menu.viewing-with-actor',
		defaultMessage: 'with {actorName} • Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing on behalf of a user.',
	},
	viewingWithActorInactive: {
		id: 'team-presence.agent-group-menu.viewing-with-actor-inactive',
		defaultMessage: 'with {actorName} • Viewing (Inactive)',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent was viewing on behalf of a user but has been inactive for at least five minutes.',
	},
});

export type AgentPresenceMenuItemProps = {
	actingUserName?: string;
	agent: AgentWithPresence;
};

export const AgentPresenceMenuItem = ({
	actingUserName,
	agent,
}: AgentPresenceMenuItemProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleAgentClick = useCallback(
		(agentId: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresenceGroupOverflowMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflowMenuItem',
					product: 'confluence',
					attributes: {
						agentId,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const getPresenceDescription = () => {
		if (!fg('platform_move_presence_agents')) {
			return undefined;
		}

		const isInactive = isAgentPresenceInactive(agent);

		if (agent.presenceActivity === 'editor') {
			return actingUserName ? (
				<FormattedMessage
					{...(isInactive ? i18n.editingWithActorInactive : i18n.editingWithActor)}
					values={{ actorName: actingUserName }}
				/>
			) : (
				<FormattedMessage {...(isInactive ? i18n.editingInactive : i18n.editing)} />
			);
		}

		return actingUserName ? (
			<FormattedMessage
				{...(isInactive ? i18n.viewingWithActorInactive : i18n.viewingWithActor)}
				values={{ actorName: actingUserName }}
			/>
		) : (
			<FormattedMessage {...(isInactive ? i18n.viewingInactive : i18n.viewing)} />
		);
	};

	return (
		<ButtonItem
			onClick={() => handleAgentClick(agent.id)}
			iconBefore={
				fg('platform_move_presence_agents') ? (
					<AgentPresenceAvatar agent={agent} size="small" showBorder />
				) : (
					<AgentPresenceAvatar agent={agent} />
				)
			}
			description={getPresenceDescription()}
		>
			{agent.name}
		</ButtonItem>
	);
};
